import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowDownRight, ArrowUpRight, PieChart as PieIcon, Store } from "lucide-react";
import { Bar, CartesianGrid, Cell, ComposedChart, Line, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { C, ChartTooltip, Grad, anim, axis, cursor } from "@/components/common/ChartKit";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { SupplierDialog } from "@/components/finance/SupplierDialog";
import { api } from "@/lib/api";
import { useFormat } from "@/lib/format";
import { cn } from "@/lib/utils";

export const CAT_COLORS = {
  alimentation: "#87B8A5", logement: "#1F2A44", energie: "#E9B872", transport: "#F2A88B", sante: "#9CC5E0",
  loisirs: "#C8A2D6", abonnements: "#6E8FB5", shopping: "#E58FA0", autre: "#B9B4A6", banque: "#8C9A7A",
};
const BAR = { ok: "bg-sage", warning: "bg-butter", over: "bg-destructive" };
const monthLabel = (m, opts = { month: "long", year: "numeric" }) => new Date(`${m}-01T12:00:00`).toLocaleDateString("fr-FR", opts);
const lastMonths = () => [...Array(6)].map((_, i) => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - i); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });

const Delta = ({ v, testid }) => v == null ? null : v === 0 ? <span className="text-xs text-muted-foreground" data-testid={testid}>= 0 %</span> : (
  <span className={cn("inline-flex items-center gap-0.5 text-xs font-semibold", v > 0 ? "text-destructive" : "text-success")} data-testid={testid}>
    {v > 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}{v > 0 ? "+" : ""}{v} %
  </span>
);

const Kpi = ({ label, value, sub, testid }) => (
  <div className="rounded-2xl border border-border bg-card/70 p-4" data-testid={testid}>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="num mt-1 text-xl font-bold sm:text-2xl">{value}</div>
    {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
  </div>
);

const Kpis = ({ d }) => {
  const { money } = useFormat();
  const m2 = (v) => money(v, { maximumFractionDigits: 0 });
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <Kpi label="Dépensé" value={m2(d.total)} sub={<span className="flex flex-wrap items-center gap-1">vs mois précédent <Delta v={d.delta_pct} testid="overview-delta" /></span>} testid="overview-total" />
      <Kpi label="Budget prévu" value={d.budget_total ? m2(d.budget_total) : "—"} sub={d.budget_total ? `${Math.round((d.total / d.budget_total) * 100)} % consommé` : "Aucun plafond"} testid="overview-budget" />
      <Kpi label="Reste à dépenser" value={d.remaining == null ? "—" : m2(d.remaining)} sub={d.remaining < 0 ? "Budget dépassé" : "sur les plafonds"} testid="overview-remaining" />
      <Kpi label="Taux d'épargne" value={d.savings_rate == null ? "—" : `${d.savings_rate} %`} sub={d.income ? `Revenus ${m2(d.income)}` : "Aucun revenu détecté"} testid="overview-savings" />
    </div>
  );
};

const Donut = ({ d }) => {
  const { money } = useFormat();
  const data = d.categories.filter((c) => c.spent > 0);
  if (!data.length) return <p className="py-16 text-center text-sm text-muted-foreground">Aucune dépense ce mois-ci.</p>;
  return (
    <div className="relative h-56" data-testid="overview-donut">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={data} dataKey="spent" nameKey="label" innerRadius="62%" outerRadius="92%" paddingAngle={2} stroke="none">
            {data.map((c) => <Cell key={c.key} fill={CAT_COLORS[c.key] || "#B9B4A6"} />)}
          </Pie>
          <Tooltip formatter={(v, n) => [money(v, { maximumFractionDigits: 2 }), n]} contentStyle={{ borderRadius: 12, border: "none", fontSize: 12 }} />
        </PieChart>
      </ResponsiveContainer>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xs text-muted-foreground">Total</span>
        <span className="num text-lg font-bold">{money(d.total, { maximumFractionDigits: 0 })}</span>
      </div>
    </div>
  );
};

const CategoryTable = ({ d }) => {
  const { money } = useFormat();
  return (
    <ul className="space-y-3" data-testid="overview-categories">
      {d.categories.map((c) => (
        <li key={c.key} className="text-sm" data-testid={`overview-cat-${c.key}`}>
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: CAT_COLORS[c.key] }} />
            <span className="min-w-0 flex-1 truncate font-medium">{c.label}</span>
            <span className="num w-14 text-right text-xs font-bold" data-testid={`overview-share-${c.key}`}>{c.share_pct.toLocaleString("fr-FR")} %</span>
            <span className="num w-20 text-right">{money(c.spent, { maximumFractionDigits: 0 })}</span>
          </div>
          <div className="mt-1 flex items-center gap-2 pl-4 text-xs text-muted-foreground">
            {c.limit ? (
              <>
                <div className="h-1.5 flex-1 rounded-full bg-secondary"><div className={cn("h-full rounded-full", BAR[c.status])} style={{ width: `${Math.min(100, c.used_pct)}%` }} /></div>
                <span className="num">{c.used_pct} % de {money(c.limit, { maximumFractionDigits: 0 })}</span>
              </>
            ) : <span className="flex-1">Sans plafond</span>}
            <Delta v={c.delta_pct} testid={`overview-cat-delta-${c.key}`} />
          </div>
        </li>
      ))}
    </ul>
  );
};

const Trend = ({ d }) => {
  const { money } = useFormat();
  const data = d.trend.map((t) => ({ ...t, label: monthLabel(t.month, { month: "short" }) }));
  return (
    <div className="h-48" data-testid="overview-trend">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 8, right: 4, left: 4, bottom: 0 }}>
          <defs><Grad id="ovBar" color={C.mint} /></defs>
          <CartesianGrid vertical={false} strokeDasharray="3 6" stroke="currentColor" strokeOpacity={0.08} />
          <XAxis dataKey="label" {...axis} />
          <Tooltip cursor={cursor} content={<ChartTooltip names={{ total: "Dépenses", budget: "Budget" }} />} />
          <Bar dataKey="total" fill="url(#ovBar)" radius={[10, 10, 3, 3]} maxBarSize={34} {...anim} />
          {d.budget_total > 0 && <Line dataKey="budget" type="monotone" stroke={C.gold} strokeDasharray="6 5" dot={false} strokeWidth={2} {...anim} />}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

const TopSuppliers = ({ d, onOpen }) => {
  const { money } = useFormat();
  if (!d.suppliers.length) return <p className="text-sm text-muted-foreground">Aucun fournisseur ce mois-ci.</p>;
  return (
    <ul className="divide-y divide-border" data-testid="overview-suppliers">
      {d.suppliers.map((s) => (
        <li key={s.key}>
          <button type="button" onClick={() => onOpen(s)} className="flex w-full items-center gap-3 py-2 text-left text-sm transition-colors hover:text-primary" data-testid={`overview-supplier-${s.key}`}>
            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: CAT_COLORS[s.category] }} />
            <span className="min-w-0 flex-1 truncate font-medium underline-offset-2 hover:underline">{s.name}</span>
            <span className="num text-xs text-muted-foreground">{s.share_pct.toLocaleString("fr-FR")} %</span>
            <span className="num w-20 text-right">{money(s.spent, { maximumFractionDigits: 0 })}</span>
          </button>
        </li>
      ))}
    </ul>
  );
};

export const BudgetOverview = ({ scope, householdId, title }) => {
  const [month, setMonth] = useState(lastMonths()[0]);
  const [open, setOpen] = useState(null);
  const { data: d, isLoading, isError } = useQuery({
    queryKey: ["overview", scope, householdId, month],
    queryFn: () => api.get("/finance/overview", { params: { scope, household_id: householdId, month } }).then((r) => r.data),
  });
  return (
    <section className="card-surface space-y-6 p-5 sm:p-6" data-testid={`overview-${scope}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="tone-chip tone-mint flex h-9 w-9 items-center justify-center rounded-xl"><PieIcon className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1"><h2 className="text-lg font-bold">{title}</h2>
          {scope === "household" && d && <p className="text-xs text-muted-foreground">Dépenses communes saisies{d.shared_accounts.length ? ` + comptes partagés : ${d.shared_accounts.join(", ")}` : " (aucun compte partagé dans ce foyer)"}</p>}</div>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="w-full sm:w-44" data-testid={`overview-month-select-${scope}`}><SelectValue /></SelectTrigger>
          <SelectContent>{lastMonths().map((m) => <SelectItem key={m} value={m} className="capitalize">{monthLabel(m)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {isError ? <p className="text-sm text-destructive" data-testid="overview-error">Données indisponibles.</p>
        : isLoading || !d ? <Skeleton className="h-72 rounded-2xl" /> : (
        <>
          <Kpis d={d} />
          <div className="grid gap-6 lg:grid-cols-[minmax(0,18rem)_1fr]"><Donut d={d} /><CategoryTable d={d} /></div>
          <div className="grid gap-6 lg:grid-cols-2">
            <div><h3 className="mb-2 text-sm font-semibold">Évolution sur 6 mois</h3><Trend d={d} /></div>
            <div><h3 className="mb-2 flex items-center gap-2 text-sm font-semibold"><Store className="h-4 w-4" /> Principaux fournisseurs du mois</h3><TopSuppliers d={d} onOpen={setOpen} /></div>
          </div>
        </>
      )}
      <SupplierDialog supplier={open} onClose={() => setOpen(null)} />
    </section>
  );
};
